import {Button, GenericSelect, Icon, TextRegular} from '@hm/fundamental'
import {mdiDelete} from '@mdi/js'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import type {
  ExtendedLimsMeasurementType,
  LimsMeasurementType
} from '../../types/LimsMeasurementType'
import type {Unit} from '../../types/Unit'
import {getLimsMeasurementTypeLabel} from '../../utils'

interface MappingRowProps {
  index: number
  isDisabled: boolean
  availableLimsTypes: LimsMeasurementType[]
  units: Unit[]
  mapping: ExtendedLimsMeasurementType
  onLimsTypeChange: (
    mappingId: number,
    currentLimsTypeId: number,
    newLimsTypeId: number,
    currentUnitId?: number
  ) => void
  onUnitChange: (mappingId: number, currentLimsTypeId: number, unitId: number | undefined) => void
  onDelete: (mappingId: number) => void
  testIdPrefix: string
}

export const MappingRow: React.FC<MappingRowProps> = ({
  index,
  isDisabled,
  availableLimsTypes,
  units,
  mapping,
  onLimsTypeChange,
  onUnitChange,
  onDelete,
  testIdPrefix
}) => {
  const {t} = useTranslation()

  const selectOptions = useMemo(() => {
    const currentOption: LimsMeasurementType = {
      id: mapping.id,
      code: mapping.code,
      name: mapping.name,
      unit: mapping.unit
    }
    return [currentOption, ...availableLimsTypes]
  }, [mapping, availableLimsTypes])

  const unitSelectValues = useMemo(
    () => [
      {value: '', label: t('measurementsSettings.noUnit')},
      ...units.map((unit) => ({value: unit.id, label: unit.name}))
    ],
    [t, units]
  )

  return (
    <div
      className="flex items-center gap-4 max-md:items-start"
      data-test-id={`${testIdPrefix}_row_${index}`}
    >
      <div className="h-8 w-8 rounded-full border border-gray-300 flex items-center justify-center">
        <TextRegular className="text-sm text-foreground_faded">{index + 1}</TextRegular>
      </div>

      <div className="flex items-center gap-4 max-md:flex-col max-md:items-start">
        <div className="min-w-[250px] max-md:min-w-0">
          <GenericSelect
            name={`${testIdPrefix}_row_${index}_lims_select`}
            value={mapping.id}
            label={t('measurementsSettings.limsMeasurementType')}
            isDisabled={isDisabled}
            className="!w-64"
            values={selectOptions.map((limsType) => ({
              value: limsType.id,
              label: getLimsMeasurementTypeLabel(limsType)
            }))}
            setValue={(value) =>
              onLimsTypeChange(mapping.mappingId, mapping.id, Number(value), mapping.assignedUnitId)
            }
            data-test-id={`${testIdPrefix}_row_${index}_lims_select`}
          />
        </div>

        {units.length === 0 ? (
          <TextRegular
            className="w-28 text-sm text-foreground_faded max-md:w-64"
            data-test-id={`${testIdPrefix}_row_${index}_no_unit`}
          >
            {t('measurementsSettings.noUnit')}
          </TextRegular>
        ) : (
          <div className="w-28 shrink-0 max-md:w-64">
            <GenericSelect
              name={`${testIdPrefix}_row_${index}_unit_select`}
              value={mapping.assignedUnitId ?? ''}
              label={t('measurementsSettings.unit')}
              isDisabled={isDisabled}
              className="!w-28 max-md:!w-64"
              values={unitSelectValues}
              setValue={(value) => {
                onUnitChange(
                  mapping.mappingId,
                  mapping.id,
                  value === '' ? undefined : Number(value)
                )
              }}
              data-test-id={`${testIdPrefix}_row_${index}_unit_select`}
            />
          </div>
        )}
      </div>

      <Button
        variant="primary"
        colorClass="text-negative"
        disabled={isDisabled}
        onClick={() => onDelete(mapping.mappingId)}
        data-test-id={`${testIdPrefix}_row_${index}_delete`}
      >
        <Icon path={mdiDelete} />
      </Button>
    </div>
  )
}
